import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { store } from './store/index.js';
import App from './App.js';
import './index.css';
import { registerServiceWorker, unregisterServiceWorkers, invalidateCache } from './serviceWorkerRegistration';
import ErrorBoundary from './shared/components/ErrorBoundary';
import { initializeDebugMode, logDebug, DebugLevel } from './shared/utils/debugMode';

// Initialize debug mode for comprehensive error logging
initializeDebugMode();

// Runtime health checks for React ecosystem
logDebug(DebugLevel.INFO, 'React Health Check', {
  version: React.version,
  available: !!React,
  buildTimestamp: import.meta.env.VITE_BUILD_TIMESTAMP || new Date().toISOString()
});

// Check React Scheduler availability (critical for concurrent features)
try {
  const scheduler = (React as any)?.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED?.Scheduler;
  console.log('Scheduler available:', !!scheduler);
  if (!scheduler) {
    console.warn('⚠️ React Scheduler not available - using fallback mode');
  } else {
    console.log('✅ React Scheduler loaded successfully');
  }
} catch (error) {
  console.error('❌ React Scheduler check failed:', error);
}

// Removed aggressive cache clearing that was causing issues
// Selective cache invalidation is now handled by proper asset versioning
// and CloudFront cache settings
// 
// Original code:
// if ('caches' in window) {
//   console.log('🗑️ Clearing all browser caches...');
//   caches.keys().then(cacheNames => {
//     cacheNames.forEach(cacheName => {
//       console.log(`Deleting cache: ${cacheName}`);
//       caches.delete(cacheName);
//     });
//   });
// }

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Failed to find the root element');

// Create a custom fallback UI for the error boundary
const ErrorFallback = ({ error }: { error?: Error }) => (
  <div style={{ 
    padding: '20px', 
    color: 'white', 
    background: '#333', 
    fontFamily: 'Arial, sans-serif',
    borderRadius: '4px',
    margin: '20px'
  }}>
    <h2>Something went wrong</h2>
    <p>An error occurred while loading the application.</p>
    {error && (
      <div>
        <p><strong>Error:</strong> {error.message}</p>
        <pre style={{ 
          background: '#222', 
          padding: '10px', 
          borderRadius: '4px',
          overflowX: 'auto' 
        }}>
          {error.stack}
        </pre>
      </div>
    )}
    <button 
      onClick={() => window.location.reload()} 
      style={{
        background: '#4a90e2',
        color: 'white',
        border: 'none',
        padding: '8px 16px',
        borderRadius: '4px',
        cursor: 'pointer',
        marginTop: '15px'
      }}
    >
      Reload Application
    </button>
  </div>
);

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <ErrorBoundary fallback={<ErrorFallback />}>
      <Provider store={store}>
        <App />
      </Provider>
    </ErrorBoundary>
  </React.StrictMode>
);

// Temporarily disable service worker registration due to MIME type issues
// This will be re-enabled once the CloudFront configuration is fixed
console.log('⚠️ Service worker registration disabled due to MIME type issues');

// Unregister any existing service workers to prevent errors
unregisterServiceWorkers().then(() => {
  console.log('🔄 Service workers unregistered');
});

// Note: Service worker registration is disabled until CloudFront is configured to serve
// service-worker.js with the correct MIME type (application/javascript)
// Force rebuild Sun Oct 26 20:07:22 PDT 2025
